import { useMemo, useState, type ChangeEvent } from "react";
import { useSubmitFantasyFamPicks } from "../lib/api/fantasy-fams";
import {
	DEFAULT_BANKROLL_USD,
	normalizeComposite,
	sharesToPct,
} from "../lib/fantasy-fam-picks";

interface FantasyFamPicksEditorProps {
	slug: string;
	// Current picks on the entity. Keys = symbol (uppercase), values = pct (0–100).
	current: Record<string, number>;
	// The Fantasy Fam's bankroll (migration 043) — the reference notional a
	// "shares" row is sized against. Falls back to DEFAULT_BANKROLL_USD when
	// non-positive (shouldn't happen, but keeps the math safe).
	bankrollUsd: number;
	// When false, render the editor in read-only mode (status !== 'active' or
	// caller isn't the PM).
	editable: boolean;
}

type PickMode = "pct" | "shares";

interface Row {
	id: string;
	symbol: string;
	value: string; // raw input — parsed at submit time
	mode: PickMode;
	// When mode='shares', resolvedPct is filled in after we fetch a quote
	// at submit. Until then it's null.
	resolvedPct?: number | null;
	priceUsd?: number | null;
	quoteError?: string | null;
}

// DEFAULT_BANKROLL_USD + the pick arithmetic live in lib/fantasy-fam-picks.ts
// so they're unit-testable without a DOM.

function newRow(): Row {
	return {
		id: crypto.randomUUID(),
		symbol: "",
		value: "",
		mode: "pct",
	};
}

function rowsFromCurrent(current: Record<string, number>): Row[] {
	const entries = Object.entries(current);
	if (entries.length === 0) return [newRow()];
	return entries.map(([symbol, pct]) => ({
		id: crypto.randomUUID(),
		symbol,
		value: pct.toString(),
		mode: "pct" as PickMode,
	}));
}

async function fetchQuotePrice(symbol: string): Promise<number | null> {
	try {
		const res = await fetch(`/api/market/quote/${encodeURIComponent(symbol)}`, {
			credentials: "include",
		});
		if (!res.ok) return null;
		const body = (await res.json()) as { last?: number };
		const last = body.last;
		if (typeof last !== "number" || !Number.isFinite(last) || last <= 0) {
			return null;
		}
		return last;
	} catch {
		return null;
	}
}

export function FantasyFamPicksEditor({
	slug,
	current,
	bankrollUsd,
	editable,
}: FantasyFamPicksEditorProps) {
	const [rows, setRows] = useState<Row[]>(() => rowsFromCurrent(current));
	const [error, setError] = useState<string | null>(null);
	const submit = useSubmitFantasyFamPicks(slug);

	// The reference bankroll a shares→pct conversion sizes against. Guard
	// against a non-positive value so the arithmetic never divides by zero.
	const bankroll = bankrollUsd > 0 ? bankrollUsd : DEFAULT_BANKROLL_USD;

	const pctTotal = useMemo(() => {
		let sum = 0;
		for (const r of rows) {
			if (r.mode === "pct") {
				const n = parseFloat(r.value);
				if (Number.isFinite(n)) sum += n;
			} else if (r.resolvedPct !== undefined && r.resolvedPct !== null) {
				sum += r.resolvedPct;
			}
		}
		return sum;
	}, [rows]);

	function updateRow(id: string, patch: Partial<Row>) {
		setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	}
	function addRow() {
		setRows((prev) => [...prev, newRow()]);
	}
	function removeRow(id: string) {
		setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);

		// First pass — validate symbols + non-empty values.
		const cleaned: { symbol: string; raw: number; mode: PickMode }[] = [];
		for (const r of rows) {
			const symbol = r.symbol.trim().toUpperCase();
			if (!symbol) continue;
			if (!/^[A-Z][A-Z0-9.-]{0,9}$/.test(symbol)) {
				setError(`"${r.symbol}" isn't a valid symbol.`);
				return;
			}
			const raw = parseFloat(r.value);
			if (!Number.isFinite(raw) || raw <= 0) {
				setError(
					`${symbol}: enter a positive ${r.mode === "pct" ? "percent" : "share count"}.`,
				);
				return;
			}
			cleaned.push({ symbol, raw, mode: r.mode });
		}
		if (cleaned.length === 0) {
			setError("Add at least one pick.");
			return;
		}

		// Second pass — resolve shares → pct via a live quote. Pct rows are
		// kept as-is. sharesToPct + the normalize step are pure helpers in
		// lib/fantasy-fam-picks.ts (unit-tested there).
		const composite: Record<string, number> = {};
		for (const c of cleaned) {
			if (c.mode === "pct") {
				composite[c.symbol] = (composite[c.symbol] ?? 0) + c.raw;
				continue;
			}
			const price = await fetchQuotePrice(c.symbol);
			if (price === null) {
				setError(
					`Couldn't fetch a current price for ${c.symbol}; switch to % allocation or remove the row.`,
				);
				return;
			}
			const pct = sharesToPct(c.raw, price, bankroll);
			composite[c.symbol] = (composite[c.symbol] ?? 0) + pct;
		}

		// Normalize to exactly 100 — Fantasy Fam scoring needs the
		// allocations to sum to 100, and shares→pct conversion drifts off
		// unless the user happened to perfectly size against the bankroll.
		const normalized = normalizeComposite(composite);
		if (Object.keys(normalized).length === 0) {
			setError("Picks total 0% — add allocations.");
			return;
		}

		try {
			await submit.mutateAsync({ composite: normalized });
			// Reset the local state to what we just sent — so the row count
			// matches `current` (re-rendered by the parent after invalidation).
			setRows(rowsFromCurrent(normalized));
		} catch (err) {
			setError(err instanceof Error ? err.message : "Submit failed.");
		}
	}

	return (
		<form onSubmit={onSubmit} className="space-y-3">
			<div className="space-y-2">
				{rows.map((row) => (
					<PickRow
						key={row.id}
						row={row}
						editable={editable}
						onChange={(patch) => updateRow(row.id, patch)}
						onRemove={() => removeRow(row.id)}
						canRemove={rows.length > 1}
					/>
				))}
			</div>

			<div className="flex flex-wrap items-center justify-between gap-2">
				<button
					type="button"
					onClick={addRow}
					disabled={!editable || submit.isPending}
					className="rounded-md border border-dashed border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-50"
				>
					+ Add pick
				</button>
				<p className="text-xs text-[var(--color-muted)]">
					%-rows total:{" "}
					<span className="font-mono">{pctTotal.toFixed(2)}%</span>
					{" — "}
					shares convert at submit time using current quote.
				</p>
			</div>

			{error && <p className="text-xs text-[var(--color-danger)]">{error}</p>}

			{editable && (
				<div className="flex items-center justify-end gap-2">
					<button
						type="submit"
						disabled={submit.isPending}
						className="rounded-md bg-[var(--color-accent)] px-4 py-1.5 text-sm font-semibold text-black hover:opacity-90 disabled:opacity-50"
					>
						{submit.isPending ? "Submitting…" : "Save picks"}
					</button>
				</div>
			)}
			<p className="text-xs text-[var(--color-muted)]">
				Allocations are normalized to 100% when you submit. Shares are sized
				against this Fantasy Fam's ${bankroll.toLocaleString()} bankroll for
				conversion.
			</p>
		</form>
	);
}

interface PickRowProps {
	row: Row;
	editable: boolean;
	onChange: (patch: Partial<Row>) => void;
	onRemove: () => void;
	canRemove: boolean;
}

function PickRow({ row, editable, onChange, onRemove, canRemove }: PickRowProps) {
	function onSymbolChange(e: ChangeEvent<HTMLInputElement>) {
		// Uppercase + strip whitespace as the user types — symbols are always
		// uppercase on the wire.
		onChange({ symbol: e.target.value.toUpperCase().replace(/\s+/g, "") });
	}
	function onValueChange(e: ChangeEvent<HTMLInputElement>) {
		onChange({ value: e.target.value });
	}
	function toggleMode() {
		// Clear the value when flipping mode so we don't accidentally treat 30
		// shares as 30%.
		onChange({ mode: row.mode === "pct" ? "shares" : "pct", value: "" });
	}

	return (
		<div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] p-2">
			<input
				type="text"
				value={row.symbol}
				onChange={onSymbolChange}
				placeholder="AAPL"
				maxLength={10}
				disabled={!editable}
				aria-label="Symbol"
				className="w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 font-mono text-sm uppercase tracking-wider placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
			/>
			<input
				type="number"
				value={row.value}
				onChange={onValueChange}
				placeholder={row.mode === "pct" ? "30" : "10"}
				min="0"
				step="any"
				disabled={!editable}
				aria-label={row.mode === "pct" ? "Percent" : "Share count"}
				className="w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 font-mono text-sm placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:opacity-50"
			/>
			<button
				type="button"
				onClick={toggleMode}
				disabled={!editable}
				title={
					row.mode === "pct"
						? "Click to switch to share-count input"
						: "Click to switch to percent input"
				}
				className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-2 py-1 text-xs uppercase tracking-wider hover:border-[var(--color-accent)] disabled:opacity-50"
			>
				{row.mode === "pct" ? "%" : "shares"}
			</button>
			{canRemove && (
				<button
					type="button"
					onClick={onRemove}
					disabled={!editable}
					aria-label="Remove pick"
					title="Remove pick"
					className="ml-auto rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-50"
				>
					×
				</button>
			)}
		</div>
	);
}
